/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import PageCanvasCss from "./PageCanvas.css";
import { LeftGrabberContainer } from "containers/document/page/grabber/LeftGrabberContainer";
import { RightGrabberContainer } from "containers/document/page/grabber/RightGrabberContainer";
import hammer from "hammerjs/hammer";
import { getOS } from "dc-preview-utils/src/Utils/DeviceUtils";
import { cursorType } from "constants/enums.js";
import { PageHighlightsContainer } from "containers/document/page/PageHighlightsContainer";
import { ReadAloudOverlayContainer } from "containers/readAloud/ReadAloudOverlayContainer";
import { HyperlinkOverlayContainer } from "containers/hyperlink/HyperlinkOverlayContainer";
import { InteractiveTooltipContainer } from "containers/interactiveTooltip/InteractiveTooltipContainer";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { isPointInsideRectangle } from "utils/PageUtils";
import { getPageAreaRects, getPagesViewXPos, getPagesViewYPos } from "selectors/pagedViewState";
import { getHudHandle, getIsHUDDocked, hudDisabled } from "selectors/hudSelectors";
import * as stores from "stores";
import { dialogOpened } from "selectors/documentSelectors";
import { shouldTriggerEnterEvent } from "selectors/pageSelectors";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { getCurrentCursor, getOverlayVisibility } from "selectors/overlayDropinSelectors";
import classNames from "classnames";
import CursorContainer from "containers/cursor/CursorContainer";
import { ACROFORM_CLIENT_NAME, FILLSIGN_CLIENT_NAME, OVERLAY_VISIBILITY } from "../../../constants/constants";
import TileCanvasContainer from "../../../containers/document/page/TileCanvasContainer";
import { TOP_OVERLAY_Z_INDEX } from "constants/constants";
import { trackCaretVisible } from "analytics/events";

class PageCanvas extends Component {
  constructor() {
    super();
    this.hManager = null;
    this.singleTap = null;
    this.doubleTap = null;
    this.panTap = null;
    this.longPress = null;
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.onFocus = this.onFocus.bind(this);

    this.lastPosX = null;
    this.lastPosY = null;
    this.isDragging = false;
  }

  handleMouseOver(evt) {
    if (dialogOpened(stores)) {
      this.props.onMouseOver(evt);
      this.props.setShouldTriggerEnterEvent(true);
    } else {
      const event = evt.nativeEvent;
      const rects = getPageAreaRects(stores);
      if (shouldTriggerEnterEvent(stores, this.props.pageIndex)) {
        const isPointInsidePageArea = isPointInsideRectangle(event.clientX, event.clientY, rects, true);
        if (isPointInsidePageArea) {
          this.props.setShouldTriggerEnterEvent(false);
          this.props.onMouseOver(evt);
        }
      }
    }
  }

  handleMouseLeave(evt) {
    if (dialogOpened(stores)) {
      this.props.onMouseLeave(evt);
      this.props.setShouldTriggerEnterEvent(true);
    } else if (!shouldTriggerEnterEvent(stores, this.props.pageIndex)) {
      const event = evt.nativeEvent;
      const rects = getPageAreaRects(stores);
      let isPointInsideHud = false;
      if (!getIsHUDDocked(stores) && !hudDisabled(stores)) {
        const hudHandle = getHudHandle(stores);
        const { left, right, top, bottom } = hudHandle.getBoundingClientRect();
        isPointInsideHud = isPointInsideRectangle(event.clientX, event.clientY, [{ left, right, top, bottom }], true);
      }
      const isPointInsidePageArea = isPointInsideRectangle(event.clientX, event.clientY, rects, false);
      if (!isPointInsidePageArea || isPointInsideHud) {
        this.props.setShouldTriggerEnterEvent(true);
        this.props.onMouseLeave(evt);
      }
    }
  }

  componentWillUnmount() {
    if (this.hManager) {
      this.hManager.off("panstart", this.handlePanTapStart.bind(this));
      this.hManager.off("panmove", this.handlePanTapMove.bind(this));
      this.hManager.off("panend pancancel", this.handlePanTapEnd.bind(this));

      this.hManager.remove(this.panTap);
      this.panTap = null;

      this.hManager.off("doubletap", this.handleDoubleTap.bind(this));
      this.hManager.remove(this.doubleTap);
      this.doubleTap = null;

      this.hManager.off("singletap", this.handleSingleTap.bind(this));
      this.hManager.remove(this.singleTap);
      this.singleTap = null;

      this.hManager.off("longpress", this.handleLongPress.bind(this));
      this.hManager.off("pressup", this.props.handleTouchEnd);
      this.hManager.remove(this.longPress);
      this.longPress = null;

      this.hManager.destroy();
      this.hManager = null;
    }
    this._removeContextMenuHandler && this._removeContextMenuHandler();
    this._removeTouchEndHandler && this._removeTouchEndHandler();
    this._removeGestureEndHandler && this._removeGestureEndHandler();
  }

  handlePanTapStart() {
    this.isDragging = true;
    this.lastPosX = getPagesViewXPos(stores);
    this.lastPosY = getPagesViewYPos(stores);
  }

  handlePanTapMove(ev) {
    var posX = -ev.deltaX + this.lastPosX;
    var posY = -ev.deltaY + this.lastPosY;
    stores.pagedViewState.scrollPagesView(posX, posY, true);
  }

  handlePanTapEnd() {
    this.isDragging = false;
    this.lastPosX = null;
    this.lastPosY = null;
  }

  handleDoubleTap(event) {
    if (event.pointerType === "touch" && this.props.getEventOnGrabber() === false) {
      this.props.handleDoubleTap(event);
    } else if (this.props.getEventOnGrabber() === true) {
      this.props.setEventOnGrabber(false);
    }
  }

  handleSingleTap(event) {
    if (this.props.getEventOnGrabber() === false) {
      if (!(getOS().name === "Mac OS" && event.changedPointers[0].ctrlKey)) {
        this.props.handleTap(event);
        if (event.srcEvent && event.srcEvent.shiftKey) return;
      }
    } else {
      this.props.setEventOnGrabber(false);
    }
  }

  handleLongPress(event) {
    if (event.pointerType === "touch" && this.props.getEventOnGrabber() === false) {
      this.props.handlePress(event);
    } else if (this.props.getEventOnGrabber() === true) {
      this.props.setEventOnGrabber(false);
    }
  }

  componentDidMount() {
    // add this to below arguments to make hammer not stop default event processing, {touchAction: "pan-x pan-y"  }
    this.hManager = new hammer.Manager(this.elementHandle);

    if (this.props.isPanActive) {
      this.panTap = new hammer.Pan({ direction: hammer.DIRECTION_ALL, threshold: 10 });
      this.hManager.add(this.panTap);
      this.hManager.on("panstart", this.handlePanTapStart.bind(this));
      this.hManager.on("panmove", this.handlePanTapMove.bind(this));
      this.hManager.on("panend pancancel", this.handlePanTapEnd.bind(this));
    }
    this.doubleTap = new hammer.Tap({
      touchAction: "manipulation",
      event: "doubletap",
      taps: 2,
      interval: 500,
      time: 300,
      threshold: 25,
      posThreshold: 30
    });
    this.hManager.add(this.doubleTap);

    this.hManager.on("doubletap", this.handleDoubleTap.bind(this));

    this.singleTap = new hammer.Tap({
      event: "singletap",
      taps: 1
    });
    this.hManager.add(this.singleTap);

    this.hManager.on("singletap", this.handleSingleTap.bind(this));

    this.longPress = new hammer.Press({
      event: "longpress",
      time: 350
    });
    this.hManager.add(this.longPress);

    this.hManager.on("longpress", this.handleLongPress.bind(this));

    this.hManager.on("pressup", this.props.handleTouchEnd);

    // please remove event listners as well if adding any listener here.
    if (this.elementHandle) {
      this._removeContextMenuHandler = addEventListener(this.elementHandle, "contextmenu", this.props.onContextMenu);
      this._removeTouchEndHandler = addEventListener(this.elementHandle, "touchend", this.props.handleTouchEnd, {
        passive: false
      });
      this._removeGestureEndHandler = addEventListener(this.elementHandle, "gestureend", this.props.handleTouchEnd);
    }
  }

  componentDidUpdate() {
    if (this.props.hasCursor) {
      trackCaretVisible();
    }
    if (this.props.isPanActive && !this.panTap) {
      this.panTap = new hammer.Pan({ direction: hammer.DIRECTION_ALL, threshold: 10 });
      this.hManager.add(this.panTap);
      this.hManager.on("panstart", this.handlePanTapStart.bind(this));
      this.hManager.on("panmove", this.handlePanTapMove.bind(this));
      this.hManager.on("panend pancancel", this.handlePanTapEnd.bind(this));
    } else if (!this.props.isPanActive && this.panTap) {
      document.querySelectorAll(`.ReactVirtualized__Grid`).forEach(elem => {
        elem.style.cursor = cursorType.DEFAULT;
      });
      document.querySelectorAll(`.${PageCanvasCss.parent}`).forEach(elem => {
        elem.style.cursor = getCurrentCursor(stores);
      });
      this.hManager.off("panstart", this.handlePanTapStart.bind(this));
      this.hManager.off("panmove", this.handlePanTapMove.bind(this));
      this.hManager.off("panend pancancel", this.handlePanTapEnd.bind(this));
      this.hManager.remove(this.panTap);
      this.panTap = null;
    }
  }

  get showOverlays() {
    return this.canvasHandle !== null;
  }

  // onMouseOver should be accompanied by onFocus for accessibility reasons as mentioned in ESLint
  onFocus() {}

  getTopOverlayStyle = clientName => {
    // once we fully enable modernViewer we can remove this function and add this css directly in render function.
    let topOverlayStyle = {
      position: !getShouldUseModernViewer(stores) && clientName === ACROFORM_CLIENT_NAME ? "static" : "absolute",
      top: 0,
      left: 0,
      zIndex: TOP_OVERLAY_Z_INDEX
    };
    return topOverlayStyle;
  };

  getVisibilityStyle = clientName => {
    const visibility = getOverlayVisibility(stores, clientName);
    let visibilityStyle = {};
    if (visibility === OVERLAY_VISIBILITY.VISIBLE) {
      visibilityStyle = { display: "block" };
    } else if (visibility === OVERLAY_VISIBILITY.HIDDEN) {
      visibilityStyle = { display: "none" };
    }
    return visibilityStyle;
  };

  getOverlayStyles({ index, pageOverlays, Overlay }) {
    let overlayStyles = {};
    if (Overlay.options?.styles) {
      overlayStyles = Overlay.options?.styles;
    }

    if (index === pageOverlays.length - 1 && Overlay.clientName !== "COMMENT") {
      overlayStyles = { ...overlayStyles, ...this.getTopOverlayStyle(Overlay.clientName) };
    }
    overlayStyles = { ...overlayStyles, ...this.getVisibilityStyle(Overlay.clientName) };
    return overlayStyles;
  }

  isPointerEventsNone = clientName => {
    if (!this.props.isMultiDocModeEnabled) return false;
    return [ACROFORM_CLIENT_NAME, FILLSIGN_CLIENT_NAME].includes(clientName);
  };

  render() {
    const {
      onClick,
      onMouseMove,
      onMouseDown,
      onMouseUp,
      onDoubleClick,
      topOverlay,
      onMouseDownCapture,
      isFitOneFullPageMode
    } = this.props;

    let newPageOverlays = null;
    let overlayDiv = null;
    let tileCanvasStyle = null;
    if (this.showOverlays) {
      const pageOverlays = this.props.pageOverlays;
      if (pageOverlays) {
        let pageProps = {
          pageIndex: this.props.pageIndex,
          origHeight: this.props.pageOrigHeight,
          origWidth: this.props.pageOrigWidth,
          curHeight: this.props.pageCurrHeight,
          curWidth: this.props.pageCurrWidth,
          viewRotation: this.props.viewRotation,
          rotation: this.props.pageRotation,
          curZoom: this.props.pageZoomLevel,
          isAJSRenditionLoded: this.props.isAJSRenditionLoded,
          currentPageIndex: this.props.currentPageIndex,
          mediaBox: this.props.boxes.mediaBox,
          cropBox: this.props.boxes.cropBox,
          theme: this.props.currentTheme
        };
        newPageOverlays = pageOverlays.map((Overlay, index) => {
          if (Overlay.clientName === "acroform") {
            pageProps.accNodePrefix = this.props.accNodePrefix;
            pageProps.accTextLoadFailed = this.props.accTextLoadFailed;
            pageProps.isFormsAccEnabled = this.props.isFormsAccEnabled;
          }

          const X = Overlay.component;
          return (
            <div
              className={classNames(PageCanvasCss.overlayComponentContainer, {
                [PageCanvasCss.fullPageGeometry]: Overlay.clientName !== ACROFORM_CLIENT_NAME,
                [PageCanvasCss.noPointerEvents]: this.isPointerEventsNone(Overlay.clientName)
              })}
              style={this.getOverlayStyles({ index, pageOverlays, Overlay })}
              inert={this.isPointerEventsNone(Overlay.clientName) ? "" : null}
              data-testid={`overlayContainer${index}`}
            >
              <X overlayDivHandle={this.overlayDivHandle} page={pageProps} api={Overlay.api} />
            </div>
          );
        });
      }

      overlayDiv = (
        <div
          className={PageCanvasCss.overlayClass}
          style={tileCanvasStyle}
          ref={handle => (this.overlayDivHandle = handle)}
        >
          {this.props.isTileCanvasLoaded && (
            <TileCanvasContainer
              height={this.props.pageCurrHeight}
              width={this.props.pageCurrWidth}
              pageIndex={this.props.pageIndex}
            />
          )}

          {newPageOverlays}
        </div>
      );
    }

    let zoomStyle = {
      left: "0px",
      top: "0px",
      zIndex: 999, // placing acroform overlay above commenting
      position: "absolute",
      transform: `scale(${this.props.pageZoomLevel})`
    };

    let cursorStyle = {};
    const viewRotationAngle = this.props.viewRotation;
    const isRotated = viewRotationAngle === 90 || viewRotationAngle === 270;
    const translatedCoordinate = !isRotated ? 0 : (this.props.pageCurrWidth - this.props.pageCurrHeight) / 2;
    if (this.props.cursorStateChanged && this.props.currentCursor) {
      cursorStyle.cursor = this.props.currentCursor;
    }
    return (
      <div
        ref={handle => {
          this.elementHandle = handle;
        }}
        onMouseOver={!this.props.shouldSkipMouseEnterLeave ? this.handleMouseOver : null}
        onFocus={this.onFocus}
        onMouseLeave={!this.props.shouldSkipMouseEnterLeave ? this.handleMouseLeave : null}
        {...{ onClick, onMouseDown, onMouseUp, onMouseMove, onDoubleClick, onMouseDownCapture }}
        className={PageCanvasCss.parent}
        style={{ ...cursorStyle }}
        data-index={this.props.pageIndex}
        id={this.props.currentPageIndex === this.props.pageIndex ? "pageview-current-page" : null}
      >
        {overlayDiv}
        {this.props.hasHighlights ? (
          <div>
            <div
              style={{
                ...zoomStyle,
                ...(topOverlay === "COMMENT" ? this.getTopOverlayStyle() : {})
              }}
            >
              <PageHighlightsContainer pageIndex={this.props.pageIndex} />
            </div>
            {this.props.isTextSelectionEnabled ? (
              <div style={{ position: "absolute", zIndex: TOP_OVERLAY_Z_INDEX }}>
                <LeftGrabberContainer grabberStyle={this.props.showGrabbers} />
                <RightGrabberContainer grabberStyle={this.props.showGrabbers} />
              </div>
            ) : null}
          </div>
        ) : null}
        {this.props.shouldRenderReadAloudOverLay && <ReadAloudOverlayContainer pageIndex={this.props.pageIndex} />}
        {this.props.shouldRenderHyperlinkOverlay && <HyperlinkOverlayContainer pageIndex={this.props.pageIndex} />}
        {this.props.hasCursor && <CursorContainer />}
        {this.props.shouldShowInteractiveTooltips && (
          <InteractiveTooltipContainer parentNode={this.elementHandle} pageIndex={this.props.pageIndex} />
        )}
      </div>
    );
  }
}

export { PageCanvas };
